import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

import AlbumPrintPackageOption from './AlbumPrintPackageOption';
import { getPurchasedPrintPackageCartItem } from '../../../../../../../../services/checkout/redux/helpers';
import { getCheckoutCart } from '../../../../../../../../services/checkout/redux/checkoutSelectors';
import {
  cartItemUpdate,
  togglePurchasedPrintPackageCartItem,
} from '../../../../../../../../services/checkout/redux/checkoutActions';
import { openModal as openModalAction } from '../../../../../../../../actions/modal-actions';
import { getAlbumMembershipGroupGreenScreenStatus } from '../../PurchasingOptions/helpers';
import AlbumPrintPackageMaxCheckoutItemsOption from './AlbumPrintPackageMaxCheckoutItemsOption';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumPrintPackages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      print_package: PropTypes.shape({
        id: PropTypes.string,
      }),
      quantity: PropTypes.number,
    }),
  ).isRequired,
  prepaidPrintPackages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      identity: PropTypes.shape({
        id: PropTypes.string,
      }),
      print_package: PropTypes.shape({
        id: PropTypes.string,
      }),
      quantity: PropTypes.number,
    }),
  ).isRequired,
  cart: PropTypes.array.isRequired,
  onCartItemUpdate: PropTypes.func.isRequired,
  onPurchasedPrintPackageToggle: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  optionProps: PropTypes.object,
  className: PropTypes.string,
  isAllPhotos: PropTypes.bool,
  albumMembershipPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  hasPurchasedPhotoPricings: PropTypes.bool,
};

const AlbumPrintPackageOptionList = ({
  albumMembershipGroup,
  albumPrintPackages,
  prepaidPrintPackages,
  cart,
  onCartItemUpdate,
  onPurchasedPrintPackageToggle,
  onCheckout,
  optionProps,
  className,
  isAllPhotos,
  albumMembershipPhoto,
  hasPurchasedPhotoPricings,
}) => {
  const classes = classNames({
    'album-print-package-option-list': true,
    [className]: !!className,
  });
  const isGreenScreenEnabled = getAlbumMembershipGroupGreenScreenStatus(
    albumMembershipGroup,
  );

  return (
    <div className={classes}>
      {prepaidPrintPackages.map(purchasedPrintPackage => {
        const {
          identity,
          quantity,
          print_package: printPackage,
        } = purchasedPrintPackage;

        const albumPrintPackage = albumPrintPackages.find(
          albumPackage => albumPackage.print_package.id === printPackage.id,
        );

        if (!albumPrintPackage) {
          return null;
        }

        if (albumPrintPackage?.print_package?.max_checkout_items) {
          return (
            <AlbumPrintPackageMaxCheckoutItemsOption
              key={purchasedPrintPackage.id}
              className={hasPurchasedPhotoPricings ? 'force-border-bottom' : ''}
              albumMembershipPhoto={albumMembershipPhoto}
              identity={identity}
              albumPrintPackage={albumPrintPackage}
              purchasedPrintPackage={purchasedPrintPackage}
              albumMembershipGroup={albumMembershipGroup}
              cartItem={getPurchasedPrintPackageCartItem(cart, {
                purchasedPrintPackageId: purchasedPrintPackage.id,
                photoId: albumMembershipPhoto?.photo?.id,
                identityId: identity?.id,
              })}
              onUpdate={onCartItemUpdate}
              onCheckout={onCheckout}
              isAllPhotos={isAllPhotos}
              isGreenScreenEnabled={isGreenScreenEnabled}
              onPurchasedPrintPackageToggle={({
                photo,
                album,
                print_background: printBackground,
                printBackgrounds,
                selectedPrintPackageItems,
              }) => {
                onPurchasedPrintPackageToggle({
                  albumPrintPackage,
                  purchasedPrintPackage,
                  albumMembershipGroup,
                  identity,
                  photo: {
                    ...photo,
                    print_background: printBackground ?? printBackgrounds?.[0],
                  },
                  album,
                  quantity,
                  // Store this, as the cart item will need to use this value.
                  isAllPhotos,
                  isGreenScreenEnabled,
                  selectedPrintPackageItems,
                });
              }}
              {...(optionProps ?? {})}
            />
          );
        }

        return (
          <AlbumPrintPackageOption
            key={purchasedPrintPackage.id}
            className={hasPurchasedPhotoPricings ? 'force-border-bottom' : ''}
            albumMembershipPhoto={albumMembershipPhoto}
            identity={identity}
            albumPrintPackage={albumPrintPackage}
            purchasedPrintPackage={purchasedPrintPackage}
            albumMembershipGroup={albumMembershipGroup}
            cartItem={getPurchasedPrintPackageCartItem(cart, {
              purchasedPrintPackageId: purchasedPrintPackage.id,
              photoId: albumMembershipPhoto?.photo?.id,
              identityId: identity?.id,
            })}
            onUpdate={onCartItemUpdate}
            onCheckout={onCheckout}
            isAllPhotos={isAllPhotos}
            isGreenScreenEnabled={isGreenScreenEnabled}
            onPurchasedPrintPackageToggle={({
              photo,
              album,
              print_background: printBackground,
              printBackgrounds,
            }) => {
              onPurchasedPrintPackageToggle({
                albumPrintPackage,
                purchasedPrintPackage,
                albumMembershipGroup,
                identity,
                photo: {
                  ...photo,
                  print_background: printBackground ?? printBackgrounds?.[0],
                },
                album,
                quantity,
                // Store this, as the cart item will need to use this value.
                isAllPhotos,
                isGreenScreenEnabled,
              });
            }}
            {...(optionProps ?? {})}
          />
        );
      })}
    </div>
  );
};

AlbumPrintPackageOptionList.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
});

const mapDispatchToProps = {
  openModal: openModalAction,
  onCartItemUpdate: cartItemUpdate,
  onPurchasedPrintPackageToggle: togglePurchasedPrintPackageCartItem,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AlbumPrintPackageOptionList);
